import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserRound, Camera, Trash2 } from "lucide-react";
import { api, errMsg, mediaUrl } from "@/lib/api";

const MAX_SKILLS = 8;
const chip = (on) => `px-3.5 h-9 rounded-full text-sm font-medium border transition-colors duration-300 ${on ? "bg-primary !text-white border-primary" : "bg-card border-border text-slate-600 hover:bg-secondary"}`;

export function AvatarUpload({ src, name, onFile, onRemove, uploading, testId = "avatar" }) {
  return (
    <div className="flex items-center gap-4">
      <span className="relative w-20 h-20 rounded-full overflow-hidden bg-secondary flex items-center justify-center shrink-0 border border-border">
        {src ? <img src={src} alt={name} className="w-full h-full object-cover" data-testid={`${testId}-preview`} /> : <UserRound size={30} strokeWidth={1.5} className="text-slate-400" />}
      </span>
      <div className="flex flex-col gap-1.5">
        <label className="btn-outline h-9 px-3 text-sm cursor-pointer w-fit" data-testid={`${testId}-label`}>
          <Camera size={14} strokeWidth={1.5} />{uploading ? "Mengunggah..." : src ? "Ganti foto" : "Pilih foto"}
          <input type="file" className="hidden" accept="image/jpeg,image/png,image/webp" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onFile(f); }} data-testid={`${testId}-file`} />
        </label>
        {src && onRemove && <button type="button" onClick={onRemove} className="text-xs text-rose-700 inline-flex items-center gap-1 w-fit" data-testid={`${testId}-remove`}><Trash2 size={12} strokeWidth={1.5} />Hapus foto</button>}
        <p className="text-xs text-slate-500">JPG/PNG/WEBP, maks. 8 MB. Foto diperkecil otomatis.</p>
      </div>
    </div>
  );
}

export function ProfileEditor({ me, onSaved }) {
  const [form, setForm] = useState({ name: me.name || "", bio: me.bio || "", skills: me.skills || [], service_ids: me.service_ids || [] });
  const [options, setOptions] = useState({ skills: [], services: [] });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    api.get("/config").then((r) => { const opts = r.data.skill_options || []; setOptions((o) => ({ ...o, skills: opts })); setForm((f) => ({ ...f, skills: f.skills.filter((s) => opts.includes(s)) })); });
    api.get("/services").then((r) => setOptions((o) => ({ ...o, services: r.data })));
  }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggle = (k, v, max) => {
    const cur = form[k];
    if (cur.includes(v)) return set(k, cur.filter((x) => x !== v));
    if (max && cur.length >= max) return toast.error(`Maksimal ${max} topik`);
    set(k, [...cur, v]);
  };
  const save = async () => {
    if (!form.skills.length) return toast.error("Pilih minimal satu topik");
    if (!form.service_ids.length) return toast.error("Pilih minimal satu layanan");
    if (form.bio.trim().length < 20) return toast.error("Bio minimal 20 karakter");
    setSaving(true);
    try { const { data } = await api.patch("/talent/me", form); toast.success("Profil disimpan"); onSaved(data); }
    catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const upload = async (file) => {
    if (file.size > 8 * 1024 * 1024) return toast.error("Ukuran foto maksimal 8 MB");
    const fd = new FormData(); fd.append("avatar", file);
    setUploading(true);
    try { const { data } = await api.post("/talent/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Foto profil diperbarui"); onSaved({ avatar_url: data.avatar_url }); }
    catch (e) { toast.error(errMsg(e)); } finally { setUploading(false); }
  };
  const remove = async () => {
    if (!window.confirm("Hapus foto profil?")) return;
    try { await api.delete("/talent/me/avatar"); toast.success("Foto dihapus"); onSaved({ avatar_url: "" }); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <details className="card-soft p-5 md:col-span-2" open data-testid="profile-editor">
      <summary className="cursor-pointer font-heading font-medium text-slate-800 inline-flex items-center gap-2"><UserRound size={16} strokeWidth={1.5} className="text-primary" />Profil publik</summary>
      <p className="mt-2 text-sm text-slate-500">Foto, nama, bio, topik, dan layanan ini yang dilihat user di katalog.</p>
      <div className="mt-4 space-y-5">
        <AvatarUpload src={mediaUrl(me.avatar_url)} name={me.name} onFile={upload} onRemove={remove} uploading={uploading} testId="profile-avatar" />
        <div className="grid gap-4 sm:grid-cols-[1fr_2fr]">
          <label className="text-xs text-slate-500">Nama panggilan<input className="input-soft mt-1" value={form.name} onChange={(e) => set("name", e.target.value)} minLength={2} maxLength={60} data-testid="profile-name-input" /></label>
          <label className="text-xs text-slate-500">Bio singkat ({form.bio.length}/600)<textarea className="input-soft mt-1 h-24 py-3" value={form.bio} onChange={(e) => set("bio", e.target.value)} maxLength={600} data-testid="profile-bio-input" /></label>
        </div>
        <div>
          <p className="text-sm font-medium text-slate-700">Topik yang nyaman kamu dengarkan <span className="text-slate-400 font-normal">({form.skills.length}/{MAX_SKILLS})</span></p>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="profile-skills">
            {options.skills.map((s) => <button type="button" key={s} onClick={() => toggle("skills", s, MAX_SKILLS)} className={chip(form.skills.includes(s))} aria-pressed={form.skills.includes(s)} data-testid={`profile-skill-${s.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>{s}</button>)}
          </div>
        </div>
        <div>
          <p className="text-sm font-medium text-slate-700">Layanan yang kamu tawarkan</p>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="profile-services">
            {options.services.map((s) => <button type="button" key={s.id} onClick={() => toggle("service_ids", s.id)} className={chip(form.service_ids.includes(s.id))} aria-pressed={form.service_ids.includes(s.id)} data-testid={`profile-service-${s.id}`}>{s.name}</button>)}
          </div>
        </div>
        <button type="button" onClick={save} disabled={saving} className="btn-primary h-10 px-5" data-testid="profile-save">{saving ? "Menyimpan..." : "Simpan profil"}</button>
      </div>
    </details>
  );
}
